"use client";

import { Icon } from "@/components/ui/icon";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

export type HintTooltipProps = {
  /** Short explanation shown on hover / focus. */
  hint: string;
  /** Accessible name for the trigger, e.g. "About Annual Revenue". */
  ariaLabel: string;
  className?: string;
};

/**
 * Small "i" trigger with a tooltip. Lives in a client component so the
 * Radix `asChild` trigger receives a client-created element — passing a
 * server-created `<button>` into the Slot renders nothing on the server
 * and causes a hydration mismatch.
 */
export function HintTooltip({ hint, ariaLabel, className }: HintTooltipProps) {
  return (
    <TooltipProvider delayDuration={0}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            className={cn(
              "inline-flex size-4 shrink-0 items-center justify-center text-foreground/25 hover:text-foreground/50",
              className,
            )}
            aria-label={ariaLabel}
          >
            <Icon icon="InfoCircleSolid" className="size-[14px]" />
          </button>
        </TooltipTrigger>
        <TooltipContent className="max-w-[260px]">{hint}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

HintTooltip.displayName = "HintTooltip";
